/**
 * A thin typed client for halyard-monitor-api.
 *
 * Every request and response shape here comes from `./schema` — generated by
 * `bun run contract:generate` from the vendored OpenAPI 3.1 document. Nothing in
 * this file re-declares a contract type. If the API changes shape, regeneration
 * turns that into a type error rather than a runtime surprise (NFR-01).
 *
 * Covered here — the anonymous surface, the session credential layer, and the
 * two monitor reads:
 *   GET  /                service identity   (anonymous)
 *   GET  /health          liveness           (anonymous)
 *   GET  /ready           check execution    (anonymous)
 *   POST /auth/login      sign in            (anonymous)
 *   POST /auth/logout     sign out           (authenticated)
 *   GET  /auth/session    who am I           (authenticated)
 *   GET  /monitors        the estate         (authenticated)
 *   GET  /monitors/{id}   one monitor        (authenticated)
 *
 * Plus the one Stage 3 operation this project has adopted:
 *   POST /monitors/{id}/check   run this monitor's check now (authenticated)
 *
 * Those four anonymous routes are the *entire* anonymous surface. Every other
 * operation in the contract answers 401 without `Authorization: Bearer`.
 *
 * There is still no client here for creating, editing, disabling, enabling or
 * deleting a monitor, nor for API keys or the audit log. Not because they do not
 * exist — they do — but because this client does not call them yet, and a
 * consumer row claiming a dependency we do not have is a false statement to the
 * drift sweep. Endpoints are added here as they are used, and the `consumes`
 * rows follow the code rather than leading it.
 */
import {
  authenticatedHeaders,
  type AuthenticatedRequest,
  type SessionCredential,
} from "./credentials.ts";
import type { components } from "./schema";

/** Response bodies, straight off the generated document. */
export type ServiceIdentityBody = components["schemas"]["ServiceIdentity"];
export type HealthBody = components["schemas"]["Health"];
export type ReadinessBody = components["schemas"]["Readiness"];
export type ProblemDocument = components["schemas"]["Problem"];
export type LoginRequestBody = components["schemas"]["LoginRequest"];
export type LoginResponseBody = components["schemas"]["LoginResponse"];
export type SessionBody = components["schemas"]["Session"];
export type MembershipBody = components["schemas"]["Membership"];
export type MonitorBody = components["schemas"]["Monitor"];
export type MonitorListBody = components["schemas"]["MonitorList"];

export type CheckExecutionBody = components["schemas"]["CheckExecution"];
export type CheckDetailBody = components["schemas"]["CheckDetail"];
export type CheckErrorClass = components["schemas"]["CheckErrorClass"];

/**
 * `TrustAnchor` — which root set a TLS verdict was reached under. **Two values,
 * and no third.**
 *
 * Its *absence* is a third observation but not a third value: no handshake
 * completed, so there is no anchor a verdict was reached under. Absent must
 * never be read as `public_roots` — that would report "held to the public roots
 * and passed" about a check that never read a certificate, and would say the
 * same thing about a monitor whose supplied anchor was refused.
 */
export type TrustAnchor = components["schemas"]["TrustAnchor"];

/**
 * The two outcome vocabularies, kept apart on purpose.
 *
 * There is **no `CheckOutcome` schema**. The Stage 3 cross-project request named
 * one; the emitted document does not contain the string. The concept lives only
 * as these two inline enums on `CheckExecution`, and merging them into a single
 * named type would throw away the resolution the API deliberately kept. See
 * `docs/contract-drift.md` §8.
 *
 * `CheckOutcome` here is the *executor's* coarse verdict — three values.
 * `RecordedCheckOutcome` is the label actually persisted on the `check_results`
 * row — **seven** values, of which this route can produce five today.
 */
export type CheckOutcome = CheckExecutionBody["outcome"];
export type RecordedCheckOutcome = CheckExecutionBody["recorded_outcome"];

/** The readiness verdict's machine-readable cause. Branch on this, not on the status line. */
export type ReadinessReason = ReadinessBody["reason"];
export type ReadinessStatus = ReadinessBody["status"];

/**
 * A monitor's current liveness, as the API stores it.
 *
 * Five values. Never a boolean, never computed here, never widened to a sixth.
 * See `MONITOR_STATES` and `monitorStateFacts` below for why each one is its own
 * value and what the UI is allowed to do with it.
 */
export type MonitorState = components["schemas"]["MonitorState"];
export type CheckKind = components["schemas"]["CheckKind"];

/**
 * The paths this client talks to. Written as literals on purpose: the app-level
 * drift sweep greps this file for the endpoint paths it has catalogued.
 *
 * `monitor` keeps its `{id}` placeholder rather than being assembled at the call
 * site. The sweep normalises `{id}` to a wildcard segment and matches the whole
 * path; a template string built out of `baseUrl` and an interpolated id has no
 * literal for it to find, so the row for `GET /monitors/{id}` would sit at
 * `absent` — reported as drift — while the call site was right there. See
 * `docs/contract-drift.md`.
 */
export const ROUTES = {
  serviceIdentity: "/",
  health: "/health",
  readiness: "/ready",
  login: "/auth/login",
  logout: "/auth/logout",
  session: "/auth/session",
  monitors: "/monitors",
  monitor: "/monitors/{id}",

  /**
   * `POST /monitors/{id}/check` — operationId `checkMonitor`.
   *
   * Written out as a literal with the `{id}` placeholder intact, not built by
   * interpolation, because the drift sweep greps this file for the path the
   * registry catalogued. An interpolated path is invisible to it and the row
   * would read as unaccounted-for. See `docs/contract-drift.md` §5.
   */
  checkMonitor: "/monitors/{id}/check",
} as const;

/** Fill `ROUTES.monitor`'s single placeholder. The literal above stays intact. */
export function monitorPath(id: string): string {
  return ROUTES.monitor.replace("{id}", encodeURIComponent(id));
}

/**
 * Fill the one path parameter in `ROUTES.checkMonitor`.
 *
 * The id is a UUID and is substituted verbatim: a value that is not a UUID gets
 * the same 404 as one that does not exist and one that belongs to another
 * organisation (REQ-703, D4, D5), so there is nothing for this client to
 * pre-validate and no distinction for it to leak by trying.
 */
export function checkMonitorPath(monitorId: string): string {
  return ROUTES.checkMonitor.replace("{id}", encodeURIComponent(monitorId));
}

/**
 * Base URL comes from the environment and has no default. Guessing a hostname
 * would mean a misconfigured deployment quietly polls the wrong service — or,
 * worse, polls nothing and looks calm about it.
 */
export const API_BASE_URL_ENV_VAR = "VITE_HALYARD_API_BASE_URL";

const READINESS_STATUSES = ["ready", "degraded", "unready"] as const satisfies readonly ReadinessStatus[];
const READINESS_REASONS = [
  "ok",
  "check_execution_overdue",
  // Added when halyard-monitor-api #6240 closed the case where a platform that
  // had never executed a single check still answered `ready`/`ok` for the whole
  // 120s grace window. It rides on `degraded`, not a fourth status, so anything
  // switching on `status` is unaffected — but this list also backs the runtime
  // guard below, so omitting it would make a real /ready body parse as malformed.
  "monitors_never_executed",
  // Added when halyard-monitor-api #6399 closed the fleet-shaped half of the same
  // hole: /ready answered `ready`/`ok` while one of two registered executors had
  // never once reported. It rides on `degraded` for the same reason
  // `monitors_never_executed` does — a capability the platform claims with no
  // evidence it was ever exercised is not health, and it is not an outage either.
  // The silent executor is named by slug in `executors.never_reported`.
  "executors_never_reported",
  "no_monitors_configured",
  "database_unavailable",
] as const satisfies readonly ReadinessReason[];

// If the contract grows a readiness status or reason this client does not know
// about, these fail to compile. That is the entire point of generating the types.
type StatusesCovered = [Exclude<ReadinessStatus, (typeof READINESS_STATUSES)[number]>] extends [never]
  ? true
  : never;
type ReasonsCovered = [Exclude<ReadinessReason, (typeof READINESS_REASONS)[number]>] extends [never]
  ? true
  : never;
const _statusesCovered: StatusesCovered = true;
const _reasonsCovered: ReasonsCovered = true;
void _statusesCovered;
void _reasonsCovered;

/**
 * The five monitor states, in full.
 *
 * There are five and there is no sixth. In particular there is **no `degraded`
 * monitor state** — `degraded` belongs to `GET /ready`, which is a verdict about
 * the platform's own check execution, not about a target. Inventing one here
 * would be exactly the thing this client exists to refuse: a state the API never
 * said, rendered as though it had.
 *
 * `disabled`, `never_run` and `stale` each mean the platform has **no current
 * evidence** about the target, for three different reasons, and none of them may
 * be rendered as health (consumer obligations §10, rules 1–3).
 */
export const MONITOR_STATES = [
  "up",
  "disabled",
  "never_run",
  "stale",
  "down",
] as const satisfies readonly MonitorState[];

const CHECK_KINDS = ["http", "tcp", "tls_certificate", "dns"] as const satisfies readonly CheckKind[];

// Same guard as the readiness pair above, for the same reason. If the contract
// grows a sixth state — or renames one — this stops compiling, and the build
// breaks before a monitor in an unknown state can be rendered as anything.
type MonitorStatesCovered = [Exclude<MonitorState, (typeof MONITOR_STATES)[number]>] extends [never]
  ? true
  : never;
type CheckKindsCovered = [Exclude<CheckKind, (typeof CHECK_KINDS)[number]>] extends [never] ? true : never;
const _monitorStatesCovered: MonitorStatesCovered = true;
const _checkKindsCovered: CheckKindsCovered = true;
void _monitorStatesCovered;
void _checkKindsCovered;

/**
 * A monitor narrowed to exactly one state.
 *
 * `MonitorBody` carries `state` as a union of five literals, which is enough for
 * the type checker but not enough for a reader: it lets `monitor.state` be
 * consumed with an `if` and a default branch that quietly swallows two of the
 * five. Splitting it into `ClassifiedMonitor` puts the five arms where the
 * compiler can count them.
 */
export type MonitorIn<S extends MonitorState> = Omit<MonitorBody, "state"> & { readonly state: S };

/** The five states as a discriminated union on `state`. Exhaustive by construction. */
export type ClassifiedMonitor =
  | MonitorIn<"up">
  | MonitorIn<"disabled">
  | MonitorIn<"never_run">
  | MonitorIn<"stale">
  | MonitorIn<"down">;

// Both directions. The first says the union names no state the contract does not
// have; the second says it misses none that it does.
type ClassifiedCoversContract = [Exclude<MonitorState, ClassifiedMonitor["state"]>] extends [never]
  ? true
  : never;
type ContractCoversClassified = [Exclude<ClassifiedMonitor["state"], MonitorState>] extends [never]
  ? true
  : never;
const _classifiedCoversContract: ClassifiedCoversContract = true;
const _contractCoversClassified: ContractCoversClassified = true;
void _classifiedCoversContract;
void _contractCoversClassified;

/**
 * What a state entitles the UI to say — and, mostly, what it does not.
 *
 * Every field here is read off the state the API stored. Nothing is derived from
 * timestamps, from `consecutive_failures`, or from how long ago `last_result_at`
 * was: health is inherited from the API's storage layer and is never recomputed
 * in the browser (consumer obligations §10, rule 5).
 */
export interface MonitorStateFacts {
  readonly state: MonitorState;
  /** True for exactly one state. `up` is the only evidence of health there is. */
  readonly isHealthy: boolean;
  /**
   * False whenever the platform has no current knowledge of the target —
   * `disabled`, `never_run` and `stale`. A false here must never be rendered
   * green, and the three must stay distinguishable from one another.
   */
  readonly hasCurrentEvidence: boolean;
  /** A short label. Distinct per state on purpose: three of them are not synonyms. */
  readonly label: string;
  /** Why this state means what it means, for a tooltip or an empty-state line. */
  readonly meaning: string;
}

/** Unreachable while the switch below covers all five. Typed `never` so the compiler checks that. */
function unhandledMonitorState(state: never): never {
  throw new Error(`Unhandled monitor state: ${String(state)}`);
}

/**
 * The five arms, written out.
 *
 * The `default` takes `never`, so adding a sixth state to the contract turns this
 * into a type error rather than a monitor that renders as nothing. That is the
 * whole reason the union above exists.
 */
export function monitorStateFacts(state: MonitorState): MonitorStateFacts {
  switch (state) {
    case "up":
      return {
        state,
        isHealthy: true,
        hasCurrentEvidence: true,
        label: "Up",
        meaning: "The last check succeeded and the next one is not overdue.",
      };
    case "down":
      return {
        state,
        isHealthy: false,
        hasCurrentEvidence: true,
        label: "Down",
        meaning: "Checks are running and failing past the monitor's failure threshold.",
      };
    case "disabled":
      return {
        state,
        isHealthy: false,
        hasCurrentEvidence: false,
        label: "Disabled",
        meaning: "Switched off deliberately. Not healthy — quiet. Nothing is being checked.",
      };
    case "never_run":
      return {
        state,
        isHealthy: false,
        hasCurrentEvidence: false,
        label: "Never run",
        meaning: "No check has ever produced a result for this monitor. Nothing is known about the target.",
      };
    case "stale":
      return {
        state,
        isHealthy: false,
        hasCurrentEvidence: false,
        label: "Stale",
        meaning: "There is history, but no recent result. The platform's knowledge of the target is out of date.",
      };
    default:
      return unhandledMonitorState(state);
  }
}

/**
 * True only for `up`.
 *
 * A named function rather than four `!==` comparisons scattered through the UI,
 * because "not down" is not the same question as "up" and the difference is the
 * three no-evidence states.
 */
export function isMonitorHealthy(monitor: Pick<MonitorBody, "state">): boolean {
  return monitorStateFacts(monitor.state).isHealthy;
}

/** The executor's coarse verdict. Three values, and `error` is not `failure`. */
export const CHECK_OUTCOMES = ["success", "failure", "error"] as const satisfies readonly CheckOutcome[];

/**
 * Every label the `check_results` row can carry. **Seven, not five.**
 *
 * `POST /monitors/{id}/check` can produce the first five today. The last two
 * arrive with Stage 4 and are modelled now, deliberately:
 *
 *  - `executor_unreachable` — the platform could not reach the executor that
 *    should have run the check. Like `platform_error`, it says nothing about the
 *    monitored target and must never render as the target being down.
 *  - `scheduler_missed` — a scheduled check that never ran. This is the ACC-2
 *    case and the shape of the February outage: it is the *absence* of a result.
 *    Anything other than "we did not check" reintroduces the failure this whole
 *    engagement exists to prevent.
 *
 * They are in the contract now, so they are in the union now. When Stage 4 starts
 * emitting them the build has already been made to account for them rather than
 * the UI quietly mis-rendering one.
 */
export const RECORDED_CHECK_OUTCOMES = [
  "success",
  "failure",
  "timeout",
  "connection_error",
  "platform_error",
  "executor_unreachable",
  "scheduler_missed",
] as const satisfies readonly RecordedCheckOutcome[];

export const CHECK_ERROR_CLASSES = [
  "dns_fail",
  "tcp_connect_refused",
  "tcp_timeout",
  "tls_fail",
  "tls_expired",
  "tls_hostname_mismatch",
] as const satisfies readonly CheckErrorClass[];

/**
 * The two trust anchors, as an allow-list the runtime guard can test against.
 *
 * `record_type` gets no equivalent on purpose: the API made it a `string` rather
 * than an enum so a new record type does not cost a contract cycle, and mirroring
 * it as a union here would re-introduce exactly that cost — plus a rendering
 * break the first time a monitor asks for something this list has not heard of.
 */
export const TRUST_ANCHORS = ["public_roots", "supplied_ca"] as const satisfies readonly TrustAnchor[];

// The exhaustiveness assertions that make the promise above mechanical. If the
// contract grows an eighth recorded label — or drops one — this file stops
// compiling. A build break is the whole point: the alternative is a value
// arriving at `interpretCheck` with no row in the table and being defaulted into
// the wrong bucket, which for `scheduler_missed` means rendering "we did not
// check" as "the target is fine".
type CheckOutcomesCovered = [Exclude<CheckOutcome, (typeof CHECK_OUTCOMES)[number]>] extends [never]
  ? true
  : never;
type RecordedCheckOutcomesCovered = [
  Exclude<RecordedCheckOutcome, (typeof RECORDED_CHECK_OUTCOMES)[number]>,
] extends [never]
  ? true
  : never;
type CheckErrorClassesCovered = [Exclude<CheckErrorClass, (typeof CHECK_ERROR_CLASSES)[number]>] extends [
  never,
]
  ? true
  : never;
type TrustAnchorsCovered = [Exclude<TrustAnchor, (typeof TRUST_ANCHORS)[number]>] extends [never]
  ? true
  : never;
const _checkOutcomesCovered: CheckOutcomesCovered = true;
const _recordedCheckOutcomesCovered: RecordedCheckOutcomesCovered = true;
const _checkErrorClassesCovered: CheckErrorClassesCovered = true;
const _trustAnchorsCovered: TrustAnchorsCovered = true;
void _checkOutcomesCovered;
void _recordedCheckOutcomesCovered;
void _checkErrorClassesCovered;
void _trustAnchorsCovered;

/**
 * How long this client will wait for a manual check before giving up.
 *
 * **It must exceed the executor's own budget**, which is 30 s overall (5 s to
 * connect). A black-holed target holds the connection for most of half a minute
 * and then the API answers 200 with an `error` outcome — a real, recorded,
 * useful result. A client on a shorter default aborts just before that answer
 * arrives and turns a legitimately slow check into a spurious failure of our own
 * making, which is the same mistake as rendering `platform_error` as the target
 * being down.
 */
export const CHECK_TRIGGER_TIMEOUT_MS = 35_000;

/**
 * The route's own rate limit, mirrored locally so a person holding down a button
 * gets a disabled control rather than a 429.
 *
 * The server's limit is deliberately naive: per monitor, derived from the newest
 * recorded result rather than an in-process counter, and it sends no
 * `Retry-After`. So there is nothing to read off the response — the interval is
 * the whole of it, and this constant is the client's copy.
 */
export const CHECK_TRIGGER_DEBOUNCE_MS = 5_000;

/** Header the API echoes the correlation id in, alongside `request_id` in the body. */
const REQUEST_ID_HEADER = "X-Request-Id";

/** The base URL was not configured. Thrown before any request is attempted. */
export class HalyardConfigurationError extends Error {
  override readonly name = "HalyardConfigurationError";
}

/**
 * A genuine API error: an RFC 9457 problem document.
 *
 * The base of the status taxonomy below. Every non-2xx problem is one of these;
 * 401, 403 and 404 additionally get a subclass, because those three mean three
 * genuinely different things to a person and must be said differently.
 *
 * What this deliberately does NOT do is invent a distinction the API withheld.
 * The API answers 404 for anything cross-organisation — on reads, writes and
 * deletes alike, byte-identical to a genuine not-found — so that a caller cannot
 * infer that a resource exists (REQ-703). This client carries that through
 * unchanged.
 */
export class HalyardProblemError extends Error {
  // Annotated `string` rather than left to infer a literal, so the taxonomy
  // subclasses below can each name themselves.
  override readonly name: string = "HalyardProblemError";
  readonly problem: ProblemDocument;
  readonly httpStatus: number;
  readonly requestId: string;

  constructor(problem: ProblemDocument, httpStatus: number) {
    super(`${problem.title} (${problem.status}) [request_id=${problem.request_id}]`);
    this.problem = problem;
    this.httpStatus = httpStatus;
    this.requestId = problem.request_id;
  }
}

/**
 * **401 — no live credential.**
 *
 * Either none was presented, or the one presented has expired by one of its two
 * clocks. The only thing the UI can offer is signing in again.
 *
 * On `POST /auth/login` a 401 means something different: the email and password
 * did not match an account. The body is identical whether the address is unknown
 * or the password did not match, and the server does equal work on both paths so
 * the timing does not separate them either. See `SIGN_IN_REJECTED_MESSAGE`.
 */
export class HalyardAuthenticationError extends HalyardProblemError {
  override readonly name = "HalyardAuthenticationError";
}

/**
 * **403 — the caller's role does not carry this capability.**
 *
 * This status is only ever about a role, never about whether something exists.
 * That makes it the one status where naming the reason is safe: the caller
 * already knows the thing is there, so saying "your role does not permit this"
 * discloses nothing new. It is also what `POST /auth/logout` and
 * `GET /auth/session` answer to an API key, which has no session behind it.
 */
export class HalyardRoleError extends HalyardProblemError {
  override readonly name = "HalyardRoleError";
}

/**
 * **404 — not found. Full stop.**
 *
 * This is the status that most needs the client to hold its nerve. The API
 * returns it both when a thing does not exist and when it belongs to another
 * organisation, identically, on purpose (REQ-703) — including for an
 * organisation named in `X-Halyard-Organisation` that the caller is not a
 * member of.
 *
 * So the UI says "not found" and stops. It must not speculate about permissions,
 * must not soften it into a 403's wording, and must not hint that the identifier
 * was real. Doing any of those defeats REQ-703 in the front end after the API
 * went to considerable trouble to honour it.
 */
export class HalyardNotFoundError extends HalyardProblemError {
  override readonly name = "HalyardNotFoundError";
}

/** Build the right member of the taxonomy for a status. */
function problemErrorFor(problem: ProblemDocument, httpStatus: number): HalyardProblemError {
  switch (httpStatus) {
    case 401:
      return new HalyardAuthenticationError(problem, httpStatus);
    case 403:
      return new HalyardRoleError(problem, httpStatus);
    case 404:
      return new HalyardNotFoundError(problem, httpStatus);
    default:
      return new HalyardProblemError(problem, httpStatus);
  }
}

/**
 * The response did not match the contract at all — an HTML error page from a
 * proxy, a truncated body, a 503 that is not a Readiness document. Kept separate
 * from HalyardProblemError so a malformed response is never dressed up as a
 * well-formed one.
 */
export class HalyardMalformedResponseError extends Error {
  override readonly name = "HalyardMalformedResponseError";
  readonly httpStatus: number;
  readonly requestId: string | null;

  constructor(message: string, httpStatus: number, requestId: string | null) {
    super(message);
    this.httpStatus = httpStatus;
    this.requestId = requestId;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isProblemDocument(value: unknown): value is ProblemDocument {
  if (!isRecord(value)) return false;
  if (typeof value["type"] !== "string") return false;
  if (typeof value["title"] !== "string") return false;
  if (typeof value["status"] !== "number") return false;
  if (typeof value["request_id"] !== "string") return false;
  if (value["detail"] !== undefined && typeof value["detail"] !== "string") return false;
  return true;
}

function isReadinessBody(value: unknown): value is ReadinessBody {
  if (!isRecord(value)) return false;
  const status = value["status"];
  const reason = value["reason"];
  if (typeof status !== "string" || !(READINESS_STATUSES as readonly string[]).includes(status)) return false;
  if (typeof reason !== "string" || !(READINESS_REASONS as readonly string[]).includes(reason)) return false;
  if (typeof value["detail"] !== "string") return false;
  if (typeof value["checked_at"] !== "string") return false;
  if (typeof value["overdue_grace_seconds"] !== "number") return false;
  if (!isRecord(value["monitors"]) || !isRecord(value["executors"])) return false;
  // `never_reported` is read by `silentExecutorSlugs`, so it is checked rather
  // than assumed: a body without it would hand a caller `undefined` where the
  // contract promises a list, and an empty list is a claim ("no executor is
  // silent") that a missing field has not earned.
  const executors = value["executors"];
  const neverReported = executors["never_reported"];
  if (!Array.isArray(neverReported) || neverReported.some((slug) => typeof slug !== "string")) {
    return false;
  }
  if (typeof executors["attribution_only"] !== "number") return false;
  return true;
}

function isHealthBody(value: unknown): value is HealthBody {
  if (!isRecord(value)) return false;
  if (value["status"] !== "ok") return false;
  if (typeof value["service"] !== "string") return false;
  if (typeof value["version"] !== "string") return false;
  if (typeof value["environment"] !== "string") return false;
  if (typeof value["uptime_seconds"] !== "number") return false;
  if (typeof value["checked_at"] !== "string") return false;
  return true;
}

function isServiceIdentityBody(value: unknown): value is ServiceIdentityBody {
  if (!isRecord(value)) return false;
  return (
    typeof value["service"] === "string" &&
    typeof value["version"] === "string" &&
    typeof value["environment"] === "string" &&
    typeof value["openapi_document"] === "string"
  );
}

/**
 * Read the API base URL from the environment.
 *
 * Exported so callers can fail at start-up with a clear message rather than on
 * the first request.
 */
function isMembership(value: unknown): value is MembershipBody {
  if (!isRecord(value)) return false;
  if (typeof value["organisation_id"] !== "string") return false;
  const role = value["role"];
  return role === "administrator" || role === "operator" || role === "readonly";
}

function isSessionBody(value: unknown): value is SessionBody {
  if (!isRecord(value)) return false;
  if (typeof value["user_id"] !== "string") return false;
  if (typeof value["email"] !== "string") return false;
  if (typeof value["session_id"] !== "string") return false;
  // Both clocks are required. A session document missing either is not one this
  // client will accept, because the whole expiry story depends on having both.
  if (typeof value["idle_expires_at"] !== "string") return false;
  if (typeof value["absolute_expires_at"] !== "string") return false;

  const organisation = value["organisation"];
  if (organisation !== null) {
    if (!isRecord(organisation)) return false;
    if (typeof organisation["id"] !== "string") return false;
    const role = organisation["role"];
    if (role !== "administrator" && role !== "operator" && role !== "readonly") return false;
  }

  const memberships = value["memberships"];
  if (!Array.isArray(memberships)) return false;
  return memberships.every(isMembership);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === "string");
}

/**
 * A `Monitor` document, field by field.
 *
 * As strict as `isSessionBody`, and strict in the same place: the fields that
 * carry meaning are required, not optional-with-a-fallback. `state` in
 * particular must be one of the five — a monitor whose state this client does
 * not recognise is a malformed response, never a monitor quietly rendered
 * without one. `last_result_at` is the only nullable field, and its null means
 * "no result has ever been recorded", which is not health (ACC-2).
 */
function isMonitorBody(value: unknown): value is MonitorBody {
  if (!isRecord(value)) return false;

  if (typeof value["id"] !== "string") return false;
  if (typeof value["organisation_id"] !== "string") return false;
  if (typeof value["name"] !== "string") return false;

  const kind = value["kind"];
  if (typeof kind !== "string" || !(CHECK_KINDS as readonly string[]).includes(kind)) return false;
  if (typeof value["target"] !== "string") return false;
  if (!isRecord(value["check_config"])) return false;

  if (typeof value["interval_seconds"] !== "number") return false;
  if (typeof value["timeout_seconds"] !== "number") return false;
  if (typeof value["failure_threshold"] !== "number") return false;
  if (typeof value["consecutive_failures"] !== "number") return false;

  if (!isStringArray(value["tags"])) return false;
  if (typeof value["public_on_status_page"] !== "boolean") return false;

  const state = value["state"];
  if (typeof state !== "string" || !(MONITOR_STATES as readonly string[]).includes(state)) return false;

  if (typeof value["state_changed_at"] !== "string") return false;
  const lastResultAt = value["last_result_at"];
  if (lastResultAt !== null && typeof lastResultAt !== "string") return false;
  if (typeof value["expected_next_check_at"] !== "string") return false;
  if (typeof value["created_at"] !== "string") return false;
  if (typeof value["updated_at"] !== "string") return false;

  return true;
}

/**
 * A `MonitorList` document.
 *
 * `total` is required, and that is the point of the shape: it is what lets a
 * caller tell "this organisation has no monitors" from "the list came back
 * empty". A body without it is rejected rather than defaulted to zero, because a
 * defaulted zero is a claim the API did not make.
 */
function isMonitorListBody(value: unknown): value is MonitorListBody {
  if (!isRecord(value)) return false;
  if (typeof value["total"] !== "number") return false;
  const monitors = value["monitors"];
  if (!Array.isArray(monitors)) return false;
  return monitors.every(isMonitorBody);
}

function isLoginResponseBody(value: unknown): value is LoginResponseBody {
  if (!isRecord(value)) return false;
  // The token is checked for being a non-empty string and nothing else. It is
  // opaque: no length assertion, no character-set assertion, no decoding. The
  // server's shape is the server's business and may change without notice.
  if (typeof value["token"] !== "string" || value["token"] === "") return false;
  return isSessionBody(value["session"]);
}

/**
 * `CheckDetail` — REQ-203's loosely-typed diagnostic bag.
 *
 * **Every key is optional and nulls are omitted**, so `{}` is a valid detail and
 * this guard accepts it. What it will not accept is a key present with the wrong
 * type, because that is a contract violation rather than an absence.
 *
 * The absence of `error_class` on an `error` outcome is *meaningful and must
 * survive this function unchanged*: it means the cause was not a network event —
 * a misconfigured target or a fault in the executor — so "we do not know" stays
 * distinguishable from "we know, and it was one of these". Defaulting it to
 * anything here would erase that distinction.
 */
function isCheckDetail(value: unknown): value is CheckDetailBody {
  if (!isRecord(value)) return false;
  if (value["resolved_ip"] !== undefined && typeof value["resolved_ip"] !== "string") return false;
  if (value["http_status"] !== undefined && typeof value["http_status"] !== "number") return false;
  if (value["response_body_head"] !== undefined && typeof value["response_body_head"] !== "string") return false;
  if (value["content_type"] !== undefined && typeof value["content_type"] !== "string") return false;
  if (value["cert_expiry"] !== undefined && typeof value["cert_expiry"] !== "string") return false;
  if (value["error_message"] !== undefined && typeof value["error_message"] !== "string") return false;
  if (value["expected_pattern_matched"] !== undefined && typeof value["expected_pattern_matched"] !== "boolean") {
    return false;
  }

  const errorClass = value["error_class"];
  if (errorClass !== undefined) {
    if (typeof errorClass !== "string") return false;
    if (!(CHECK_ERROR_CLASSES as readonly string[]).includes(errorClass)) return false;
  }

  // `record_type` is checked as a *string and nothing more*. The API made it a
  // string rather than an enum so a new record type does not cost a contract
  // cycle; testing it against a local allow-list here would spend that cycle
  // anyway, and would turn the first monitor to ask for an unfamiliar type into
  // a malformed-response error instead of a row that renders.
  if (value["record_type"] !== undefined && typeof value["record_type"] !== "string") return false;

  // Present-but-empty is a legitimate value (NODATA), so the array is validated
  // for element type and NOT for length. Absence is checked for separately and
  // left alone — see `answerSection` below.
  if (!isOptionalStringArray(value["answers"])) return false;
  if (!isOptionalStringArray(value["trust_anchor_fingerprints"])) return false;

  const trustAnchor = value["trust_anchor"];
  if (trustAnchor !== undefined) {
    if (typeof trustAnchor !== "string") return false;
    // A value outside the two-name allow-list is a contract violation, and it is
    // refused rather than defaulted. Defaulting an unrecognised anchor to
    // `public_roots` would report a root store the handshake never used; passing
    // it through would hand the UI a value nothing downstream can render.
    if (!(TRUST_ANCHORS as readonly string[]).includes(trustAnchor)) return false;
  }

  return true;
}

/** Absent, or an array whose every element is a string. Anything else is a violation. */
function isOptionalStringArray(value: unknown): boolean {
  if (value === undefined) return true;
  if (!Array.isArray(value)) return false;
  return value.every((element) => typeof element === "string");
}

/**
 * `CheckExecution` — the body of a 200 from `POST /monitors/{id}/check`.
 *
 * Note `result_id`: it is checked as an **integer, not a UUID**. Every other id
 * in this API is a UUID, and a client that assumes the convention holds here is
 * wrong — `check_results.id` is a `bigint GENERATED ALWAYS AS IDENTITY`,
 * serialised as a JSON number. It is also never independently addressable, so
 * there is no deep link to build from it.
 */
function isCheckExecutionBody(value: unknown): value is CheckExecutionBody {
  if (!isRecord(value)) return false;

  const resultId = value["result_id"];
  if (typeof resultId !== "number" || !Number.isInteger(resultId)) return false;

  const outcome = value["outcome"];
  if (typeof outcome !== "string" || !(CHECK_OUTCOMES as readonly string[]).includes(outcome)) return false;

  const recorded = value["recorded_outcome"];
  if (typeof recorded !== "string" || !(RECORDED_CHECK_OUTCOMES as readonly string[]).includes(recorded)) {
    return false;
  }

  const duration = value["duration_ms"];
  if (typeof duration !== "number" || !Number.isInteger(duration) || duration < 0) return false;

  // Required, even though every key inside it is optional.
  return isCheckDetail(value["detail"]);
}

/**
 * What the UI is allowed to say about a failure.
 *
 * | Status | Meaning                                  | What may be said            |
 * |--------|------------------------------------------|-----------------------------|
 * | 401    | No or expired credential                 | Sign in again               |
 * | 403    | The caller's role is short a capability   | Name the permission         |
 * | 404    | Not found                                | Nothing more. Do not guess. |
 */
export type ProblemAudience =
  | "sign_in_again"
  /** Safe to name the permission: 403 is never about existence. */
  | "role_insufficient"
  /** Say only that it was not found. */
  | "not_found"
  /** 409 from `POST /monitors/{id}/check`: the monitor is switched off. Offer enable; never auto-enable. */
  | "monitor_disabled"
  /** 429 from `POST /monitors/{id}/check`: checked under five seconds ago. */
  | "checked_too_recently"
  /** Anything else — fall back to the problem document's own title. */
  | "other";

export interface SafeMessage {
  readonly audience: ProblemAudience;
  readonly text: string;
  /** Only ever true for 403. */
  readonly mayNameThePermission: boolean;
}

/**
 * The message shown when signing in did not work.
 *
 * One message, for both an address with no account and a password that did not
 * match, because the API returns one body for both and does equal work on each
 * path. Splitting it here would hand back the account-enumeration oracle the API
 * refuses to give (REQ-703).
 */
export const SIGN_IN_REJECTED_MESSAGE = "That email address and password did not match an account.";

export interface SafeMessageOptions {
  /** Set when the failing call was `POST /auth/login`, where 401 means something else. */
  readonly during?: "sign_in";
}

/** Turn an error from this client into text that is safe to render. */
export function uiSafeMessage(error: unknown, options: SafeMessageOptions = {}): SafeMessage {
  if (error instanceof HalyardAuthenticationError) {
    return options.during === "sign_in"
      ? { audience: "other", text: SIGN_IN_REJECTED_MESSAGE, mayNameThePermission: false }
      : {
          audience: "sign_in_again",
          text: "Your session has ended. Sign in again.",
          mayNameThePermission: false,
        };
  }

  if (error instanceof HalyardRoleError) {
    // 403 is a statement about the caller's role and nothing else, so naming it
    // discloses nothing about what does or does not exist.
    return {
      audience: "role_insufficient",
      text: "Your role does not permit this.",
      mayNameThePermission: true,
    };
  }

  if (error instanceof HalyardNotFoundError) {
    // Say this and stop. No speculation about permissions, no softer synonym, no
    // hint that the identifier was real. See HalyardNotFoundError.
    return { audience: "not_found", text: "Not found.", mayNameThePermission: false };
  }

  if (error instanceof HalyardProblemError && error.httpStatus === 409) {
    // The monitor is disabled and was NOT executed. Offer to enable it; do not
    // enable it here. And re-enabling restores the schedule, not the evidence —
    // a re-enabled monitor returns to `never_run` or `stale`, never to `up`.
    return {
      audience: "monitor_disabled",
      text: "This monitor is disabled, so no check was run.",
      mayNameThePermission: false,
    };
  }

  if (error instanceof HalyardProblemError && error.httpStatus === 429) {
    // Naive, per-monitor, and it carries no Retry-After — there is nothing to
    // read off the response, so the wait is `CHECK_TRIGGER_DEBOUNCE_MS`.
    return {
      audience: "checked_too_recently",
      text: "This monitor was checked moments ago. Wait a few seconds and try again.",
      mayNameThePermission: false,
    };
  }

  if (error instanceof HalyardProblemError) {
    return { audience: "other", text: error.problem.title, mayNameThePermission: false };
  }

  return { audience: "other", text: "The request could not be completed.", mayNameThePermission: false };
}

/**
 * Who a check result is *about*.
 *
 * The single most important distinction in this file. `outcome: "error"` covers
 * two utterly different situations and the status line covers neither:
 *
 *  - `timeout` / `connection_error` — we reached out and the target did not
 *    answer. Evidence about the target.
 *  - `platform_error` / `executor_unreachable` / `scheduler_missed` — **our side
 *    failed**. No verdict about the target was obtained at all.
 *
 * Collapsing the second group into "the target is down" invents an outage. And
 * collapsing it into "fine" hides one. Neither is available: it is its own
 * answer, and `"not_checked"` is what the UI must say.
 */
export type CheckAttribution =
  /** The result is evidence about the monitored target. */
  | "target"
  /** The platform failed. Says nothing about the target, in either direction. */
  | "platform"
  /** No check ran. The absence of a result — never a result. */
  | "not_checked";

/**
 * What a `CheckExecution` actually means, with the two vocabularies reconciled.
 *
 * The UI branches on this, never on `response.status` and never on `outcome`
 * alone. Build it with `interpretCheck`.
 */
export interface CheckInterpretation {
  readonly outcome: CheckOutcome;
  readonly recordedOutcome: RecordedCheckOutcome;
  readonly attribution: CheckAttribution;
  /**
   * True only when this result says something about the monitored target.
   * False for all three of our own failures — the ACC-2 guarantee in one field.
   */
  readonly evidenceAboutTarget: boolean;
  /** True only when the target itself is at fault. Never true for a platform failure. */
  readonly targetAtFault: boolean;
  /** True only for a check that ran and passed. Never true in the absence of a result. */
  readonly healthy: boolean;
  /** Plain text. Safe to render as-is; contains nothing from the monitored target. */
  readonly text: string;
}

/**
 * The full mapping. Seven rows because there are seven labels — the two Stage 4
 * ones included, so they cannot arrive with no row and be defaulted.
 *
 * | recorded_outcome       | attribution   | about the target?          |
 * |------------------------|---------------|----------------------------|
 * | `success`              | `target`      | answered, matched          |
 * | `failure`              | `target`      | answered, answer was wrong |
 * | `timeout`              | `target`      | did not answer in time     |
 * | `connection_error`     | `target`      | could not be reached       |
 * | `platform_error`       | `platform`    | **nothing**                |
 * | `executor_unreachable` | `platform`    | **nothing**                |
 * | `scheduler_missed`     | `not_checked` | **nothing — none was run** |
 */
const CHECK_INTERPRETATIONS: Readonly<
  Record<RecordedCheckOutcome, Omit<CheckInterpretation, "outcome" | "recordedOutcome">>
> = {
  success: {
    attribution: "target",
    evidenceAboutTarget: true,
    targetAtFault: false,
    healthy: true,
    text: "The target answered and matched expectations.",
  },
  failure: {
    attribution: "target",
    evidenceAboutTarget: true,
    targetAtFault: true,
    healthy: false,
    text: "The target answered, and the answer was wrong.",
  },
  timeout: {
    attribution: "target",
    evidenceAboutTarget: true,
    targetAtFault: true,
    healthy: false,
    text: "The target did not answer in time.",
  },
  connection_error: {
    attribution: "target",
    evidenceAboutTarget: true,
    targetAtFault: true,
    healthy: false,
    text: "The target could not be reached.",
  },
  platform_error: {
    // Our fault: a bad monitor configuration or a bug in their executor. The
    // target was never given a chance to answer, so nothing here is about it.
    attribution: "platform",
    evidenceAboutTarget: false,
    targetAtFault: false,
    healthy: false,
    text: "We could not complete this check. This says nothing about the target.",
  },
  executor_unreachable: {
    // Stage 4. The platform could not reach the executor that should have run
    // the check — one hop short of the target, and equally silent about it.
    attribution: "platform",
    evidenceAboutTarget: false,
    targetAtFault: false,
    healthy: false,
    text: "We could not reach the executor for this check. This says nothing about the target.",
  },
  scheduler_missed: {
    // Stage 4, and the shape of the February outage. A scheduled check that
    // never ran. It is the absence of a result and must be said as one: not
    // "down", not "up", not a blank cell — "we did not check".
    attribution: "not_checked",
    evidenceAboutTarget: false,
    targetAtFault: false,
    healthy: false,
    text: "We did not check. No result was recorded for this interval.",
  },
};

/** Reconcile a `CheckExecution`'s two outcome fields into something renderable. */
export function interpretCheck(execution: CheckExecutionBody): CheckInterpretation {
  const row = CHECK_INTERPRETATIONS[execution.recorded_outcome];
  return { outcome: execution.outcome, recordedOutcome: execution.recorded_outcome, ...row };
}

/**
 * `detail.response_body_head` — the first 4 KiB of an **arbitrary monitored
 * target's** response body, decoded utf-8-lossy and possibly ending
 * mid-character.
 *
 * It is attacker-influenced: anyone who can make a monitor point at a host they
 * control chooses its contents. So it is carried in a wrapper that has exactly
 * one field a renderer can reach — `text` — and no `html`, no `__html`, and
 * nothing that would fit `dangerouslySetInnerHTML`. Markup inside it is left
 * **verbatim**: scrubbing it would be a second lie on top of the first, showing
 * an operator a body the target did not send. Render it as text.
 */
export interface UntrustedText {
  readonly kind: "untrusted_text";
  readonly text: string;
  /** True at the 4 KiB ceiling: the tail was cut and may end mid-character. */
  readonly truncated: boolean;
}

/** The 4 KiB ceiling the API applies before it sends the body head. */
export const RESPONSE_BODY_HEAD_MAX_BYTES = 4096;

/**
 * Wrap `detail.response_body_head`, or null when the target sent no body — which
 * is a different fact from an empty one and is kept distinguishable.
 */
export function responseBodyHead(detail: CheckDetailBody): UntrustedText | null {
  const head = detail.response_body_head;
  if (head === undefined) return null;
  return {
    kind: "untrusted_text",
    text: head,
    truncated: new TextEncoder().encode(head).length >= RESPONSE_BODY_HEAD_MAX_BYTES,
  };
}

/**
 * `detail.record_type` — the record type a `dns` check actually asked for, or
 * null when no query went out.
 *
 * Returned as an **unnarrowed string**. The API deliberately typed this as a
 * string rather than an enum so the set of record types can grow without a
 * contract cycle; a union here would spend that cycle anyway and would make the
 * first `HTTPS` or `SVCB` monitor render as a bug. Whatever the executor asked
 * for is what comes back.
 *
 * Null means the check never asked — an unreadable target, an address where a
 * name was wanted, a `check_config.record_type` the executor refuses — which is
 * not the same as asking and getting nothing back. That second case is
 * `answerSection`'s to describe.
 */
export function queriedRecordType(detail: CheckDetailBody): string | null {
  return detail.record_type ?? null;
}

/**
 * What a `dns` check learned from the answer section — as **three** observations,
 * not two.
 *
 * This is the distinction the API went to the wire to buy, and the type exists so
 * a call site cannot erase it with `answers ?? []`:
 *
 *  - `not_read` — the key was absent. No answer section was ever read: a timeout,
 *    a REFUSED, an NXDOMAIN, or a truncated reply this check does not retry over
 *    TCP. We do not know what the name publishes.
 *  - `nodata` — the key was `[]`. The resolver answered NOERROR with an empty
 *    answer section: the name exists and holds **no record of this type**. That
 *    is knowledge, and #6288 made it a `failure` about the target.
 *  - `records` — one or more answers, verbatim and unfiltered, including any the
 *    check itself judged unusable.
 *
 * Rendering `not_read` and `nodata` as one "no answers" line would report "the
 * name publishes nothing" about a resolver that never answered.
 */
export type DnsAnswerSection =
  | { readonly kind: "not_read" }
  | { readonly kind: "nodata" }
  | { readonly kind: "records"; readonly records: readonly UntrustedText[] };

export function answerSection(detail: CheckDetailBody): DnsAnswerSection {
  const answers = detail.answers;
  if (answers === undefined) return { kind: "not_read" };
  if (answers.length === 0) return { kind: "nodata" };
  // Every element is a record an arbitrary zone published, so it gets the same
  // wrapper `response_body_head` gets: one reachable text field, no HTML sink,
  // contents left verbatim.
  return {
    kind: "records",
    records: answers.map((record) => ({
      kind: "untrusted_text",
      text: record,
      truncated: false,
    })),
  };
}

/**
 * Which trust anchor a TLS verdict was reached under — as **three** observations,
 * because absence is one of them.
 *
 *  - `no_handshake` — the key was absent. No handshake completed, so there is no
 *    anchor a verdict was reached under. This is emphatically **not**
 *    `public_roots`: it is also what a monitor whose supplied anchor was refused
 *    looks like, and reporting the default root store for either would describe a
 *    verification that never happened.
 *  - `public_roots` — the runtime default root store.
 *  - `supplied_ca` — a nominated anchor replaced it, carrying the SHA-256
 *    fingerprints that make it auditable. The PEM is not on the row by design:
 *    the anchor is configuration and already lives in `check_config`.
 */
export type TlsTrustAnchor =
  | { readonly kind: "no_handshake" }
  | { readonly kind: "public_roots" }
  | { readonly kind: "supplied_ca"; readonly fingerprints: readonly string[] };

/** Unreachable while the switch below covers both anchors. Typed `never` so the compiler checks that. */
function unhandledTrustAnchor(anchor: never): never {
  throw new Error(`Unhandled trust anchor: ${String(anchor)}`);
}

/**
 * Read `detail.trust_anchor`.
 *
 * The `default` takes `never`, so a third anchor arriving in the contract stops
 * this file compiling rather than falling through to a bucket it does not belong
 * in — the same gate `monitorStateFacts` and the recorded-outcome table use.
 */
export function trustAnchorUsed(detail: CheckDetailBody): TlsTrustAnchor {
  const anchor = detail.trust_anchor;
  if (anchor === undefined) return { kind: "no_handshake" };

  switch (anchor) {
    case "public_roots":
      return { kind: "public_roots" };
    case "supplied_ca":
      // Present exactly when the anchor was supplied — but read defensively, so a
      // row missing them degrades to an empty list rather than throwing. An empty
      // list here is still `supplied_ca`: which anchor was used is the finding,
      // the fingerprints are the audit trail for it.
      return { kind: "supplied_ca", fingerprints: detail.trust_anchor_fingerprints ?? [] };
    default:
      return unhandledTrustAnchor(anchor);
  }
}

export function resolveApiBaseUrl(env?: Readonly<Record<string, string | undefined>>): string {
  const source = env ?? (import.meta.env as unknown as Record<string, string | undefined>);
  const raw = source[API_BASE_URL_ENV_VAR];

  if (raw === undefined || raw.trim() === "") {
    throw new HalyardConfigurationError(
      `${API_BASE_URL_ENV_VAR} is not set. This client has no default base URL: ` +
        `pointing at a guessed hostname would turn a misconfiguration into a silent one. ` +
        `Set it to the origin serving halyard-monitor-api.`,
    );
  }

  return raw.trim().replace(/\/+$/, "");
}

export interface HalyardClientOptions {
  /** Overrides the environment variable. Mostly for tests. */
  readonly baseUrl?: string;
  /** Injectable fetch, so tests never touch the network. */
  readonly fetch?: typeof globalThis.fetch;
}

export interface HalyardClient {
  readonly baseUrl: string;
  /** GET / — identifies the service. Not a health signal. */
  getServiceIdentity(signal?: AbortSignal): Promise<ServiceIdentityBody>;
  /**
   * GET /health — liveness only. A 200 here is compatible with every check in
   * the estate having stopped running; it says nothing about the estate.
   */
  getHealth(signal?: AbortSignal): Promise<HealthBody>;
  /**
   * GET /ready — whether check execution is current.
   *
   * Returns the parsed body for BOTH 200 and 503. `degraded`
   * (`no_monitors_configured`, `monitors_never_executed`,
   * `executors_never_reported`) and `unready` (`check_execution_overdue`,
   * `database_unavailable`) are served as 503, and that is correct behaviour,
   * not a transport failure. Throwing on `!res.ok` here would discard the one
   * field that says what is wrong.
   *
   * Six reasons, three statuses. Decide with `classifyReadinessReason`, which is
   * total and treats a reason this build has never heard of as not-ready.
   */
  getReadiness(signal?: AbortSignal): Promise<ReadinessBody>;

  /**
   * POST /auth/login — exchange an email and password for a session.
   *
   * The only anonymous route that is not a status probe. Returns the opaque token
   * and the session it names. **The token is in this response and nowhere else,
   * ever**: the server keeps only a SHA-256 digest and cannot recover it, so if
   * the caller loses it the only remedy is signing in again.
   *
   * A 401 here rejects the pair as a pair. It is deliberately identical whether
   * the address is unknown or the password did not match — render
   * `SIGN_IN_REJECTED_MESSAGE`, never a guess about which half was wrong.
   */
  logIn(credentials: LoginRequestBody, signal?: AbortSignal): Promise<LoginResponseBody>;

  /**
   * POST /auth/logout — revoke the session behind the presented credential.
   *
   * Immediate, unconditional and idempotent: signing out of an already-revoked
   * session is a 204. Resolves on 204.
   *
   * A 401 here is not a failure a person needs to see — it means there was no
   * live session to end, which is the outcome they asked for. A 403 means the
   * credential was an API key, which has no session to sign out of.
   */
  logOut(request: AuthenticatedRequest): Promise<void>;

  /**
   * GET /auth/session — who the credential resolves to, both expiry timestamps,
   * and the caller's memberships.
   *
   * **Do not poll this as a keepalive.** It is an authenticated request, so it
   * slides the idle clock exactly like any other; on a timer it would keep a
   * session alive up to the absolute ceiling and quietly undo REQ-701's idle
   * expiry. Call it on sign-in, on an explicit refresh, and when building an
   * organisation picker — not on an interval. `./session-lifetime.ts` tracks both
   * clocks locally so nothing has to.
   *
   * `organisation` comes back populated when the request named one the caller is
   * a member of, or when they belong to exactly one; null otherwise. This route
   * does not require a scope, which is what makes it usable to find out which
   * organisations there are to choose between.
   */
  getSession(request: AuthenticatedRequest): Promise<SessionBody>;

  /**
   * GET /monitors — every monitor in the organisation this request acts within,
   * and no others (REQ-703).
   *
   * Returns the whole `MonitorList`, `total` included, rather than just the
   * array. `total` is the field that separates "this organisation has no
   * monitors" from "the list is empty here", and a caller that only ever sees
   * `monitors.length` cannot tell those apart — which is precisely the mistake
   * ACC-2 is about. Absence of data is never health.
   *
   * **Do not put this on a timer.** It is an authenticated request and slides
   * the idle clock like any other; polling it would hold a session open to the
   * absolute ceiling and undo REQ-701. Call it on load and on an explicit
   * refresh.
   *
   * 401 means the credential is gone or expired; 403 means the caller's role in
   * this organisation does not carry the read; 404 means the organisation named
   * in `X-Halyard-Organisation` is not one they are a member of — and that 404
   * is byte-identical to a genuine not-found, so it must not be softened.
   */
  listMonitors(request: AuthenticatedRequest): Promise<MonitorListBody>;

  /**
   * GET /monitors/{id} — one monitor.
   *
   * A monitor belonging to another organisation answers **404, not 403**,
   * identically to an id that exists nowhere and to one that is not a UUID at
   * all (REQ-703, D4, D5). The client carries that through untouched: see
   * `HalyardNotFoundError`. Rendering "Forbidden" here — or anything that hints
   * the identifier was real — hands back the enumeration oracle the API went to
   * trouble to withhold.
   */
  getMonitor(id: string, request: AuthenticatedRequest): Promise<MonitorBody>;

  /**
   * POST /monitors/{id}/check — run this monitor's check now, synchronously.
   *
   * **There is no request body.** A trigger that let the caller name a target
   * would be a request-forgery primitive behind an authenticated API, and a row
   * recorded against a monitor must be a result *for* that monitor. Nothing is
   * sent, and nothing may be overridden per call.
   *
   * **A 200 does not mean the check passed.** The status line says only that
   * *they executed a check*; `outcome` says what it found, and it is frequently
   * `failure` or `error`. A client that reads 200 as healthy renders every
   * timeout and DNS failure as a green monitor. Render `interpretCheck(...)`,
   * never the status.
   *
   * The attempt is recorded whatever happens, including when the platform is the
   * reason it failed (REQ-202).
   *
   * **A manual check now advances the monitor.** Upstream Stage 4a.6 (4501c04)
   * landed 4a.4's writeback: `checkMonitorHandler` calls `applyCheckToMonitor`,
   * so this route feeds `state` and `consecutive_failures` exactly as a scheduled
   * check does. It is no longer fire-and-forget, and the returned
   * `CheckExecution` is *not* the monitor — re-read `GET /monitors/{id}` for the
   * new state rather than deriving one here.
   *
   * Note that the vendored contract's own prose for this operation still says
   * `state` is not changed. That sentence is stale against their implementation;
   * it is recorded as a finding for halyard-monitor-api rather than edited here,
   * because `contract/openapi.json` is their document, not ours.
   *
   * **A monitor can sit at `up` with its latest check `failure`** while the
   * failure count is under the threshold. `state` and the last result are two
   * different observations and must not be rendered as one.
   *
   * Statuses beyond the usual taxonomy:
   *  - **409** — the monitor is `disabled` and was not executed. Offer to enable
   *    it; do not enable it here. Re-enabling restores the schedule, not the
   *    evidence: it lands on `never_run` or `stale`, never on `up`.
   *  - **429** — checked under five seconds ago. No `Retry-After` to read; wait
   *    `CHECK_TRIGGER_DEBOUNCE_MS`.
   *  - **403 vs 404** — a `readonly` member of the owning organisation gets 403,
   *    which is safe to name as a role denial. A caller from another
   *    organisation gets 404, identical to an unknown id and to one that is not
   *    a UUID. Never soften that 404 (REQ-703).
   *
   * Requires `monitor:write` — administrator or operator (REQ-702).
   */
  checkMonitor(request: CheckMonitorRequest): Promise<CheckExecutionBody>;
}

/** What `checkMonitor` needs: a credential, an organisation scope, and the monitor. */
export interface CheckMonitorRequest extends AuthenticatedRequest {
  /** The monitor's UUID. Substituted into the path literal verbatim. */
  readonly monitorId: string;
}

/**
 * What a `reason` means for a caller that has to decide something.
 *
 * Two values, not six, because there are only two things a UI can do with this:
 * report that checks are running, or report that they are not. The six reasons
 * differ in *why*, which is a matter for the prose the operator reads, not for
 * the branch that decides whether anything may be coloured green.
 */
export type ReadinessVerdict = "ready" | "not_ready";

/**
 * Classify a readiness reason. Total, in both senses.
 *
 * *At compile time*: every reason in the contract has an arm, and the `default`
 * arm's `never` binding fails to compile the day upstream adds a seventh. That
 * is the same guard `READINESS_REASONS` carries, moved to where the decision is
 * actually made.
 *
 * *At run time*: the `default` arm still returns an answer, and that answer is
 * `not_ready`. This matters because the compile-time guard only protects a build
 * that has been regenerated. A build shipped before upstream added a reason will
 * meet that reason in a live response, and the only safe reading of a reason this
 * client has never heard of is that the platform is telling us something is wrong
 * in a vocabulary we do not have. Absence of understanding is not evidence of
 * health — which is the entire thesis of the endpoint being classified.
 *
 * #6399 is the case in point: `executors_never_reported` shipped upstream and was
 * live in production before this repo knew the word. Had the default arm here read
 * `ready`, this client would have rendered a platform with a silent executor as
 * green for as long as it took someone to notice.
 */
export function classifyReadinessReason(reason: ReadinessReason): ReadinessVerdict {
  switch (reason) {
    case "ok":
      return "ready";
    case "check_execution_overdue":
    case "monitors_never_executed":
    case "executors_never_reported":
    case "no_monitors_configured":
    case "database_unavailable":
      return "not_ready";
    default: {
      // Compile time: narrowed to `never` only while the arms above are exhaustive.
      const unhandled: never = reason;
      void unhandled;
      // Run time: an off-contract string reaching this line is not ready.
      return "not_ready";
    }
  }
}

/**
 * True only when the platform is actually executing checks on schedule.
 *
 * Both halves must agree. `status` is checked positively — `ready`, not "not
 * unready" — so a status this build does not know reads as not-healthy too.
 */
export function isReadinessHealthy(readiness: ReadinessBody): boolean {
  return readiness.status === "ready" && classifyReadinessReason(readiness.reason) === "ready";
}

/**
 * The executors named in `executors.never_reported`.
 *
 * Exposed so an **operator-authenticated** view can say which runner is silent.
 * These are slugs, and a slug can name a physical location (`london-1`), so
 * REQ-504 governs where they may be rendered regardless of the fact that upstream
 * publishes them unauthenticated. Consuming a field is not publishing it: this
 * must not reach the public status page.
 */
export function silentExecutorSlugs(readiness: ReadinessBody): readonly string[] {
  return readiness.executors.never_reported;
}

function requestIdFrom(response: Response): string | null {
  return response.headers.get(REQUEST_ID_HEADER);
}

async function parseJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    throw new HalyardMalformedResponseError(
      `Response body from the API was not valid JSON (HTTP ${response.status}).`,
      response.status,
      requestIdFrom(response),
    );
  }
}

/** Turn a non-2xx response into the one error type, preserving the correlation id. */
async function problemErrorFrom(response: Response): Promise<never> {
  const body = await parseJson(response);

  if (!isProblemDocument(body)) {
    throw new HalyardMalformedResponseError(
      `HTTP ${response.status} from the API was not an RFC 9457 problem document.`,
      response.status,
      requestIdFrom(response),
    );
  }

  throw problemErrorFor(body, response.status);
}

export function createHalyardClient(options: HalyardClientOptions = {}): HalyardClient {
  const baseUrl = options.baseUrl === undefined ? resolveApiBaseUrl() : options.baseUrl.replace(/\/+$/, "");
  const doFetch = options.fetch ?? globalThis.fetch;

  const url = (route: string): string => (route === "/" ? `${baseUrl}/` : `${baseUrl}${route}`);

  const get = async (route: string, signal?: AbortSignal): Promise<Response> =>
    doFetch(url(route), {
      method: "GET",
      headers: { Accept: "application/json" },
      ...(signal === undefined ? {} : { signal }),
    });

  /**
   * A GET that presents the credential. The headers are built by
   * `authenticatedHeaders` and nowhere else, so the organisation header is
   * omitted rather than emptied when no organisation was chosen.
   */
  const authenticatedGet = async (route: string, request: AuthenticatedRequest): Promise<Response> =>
    doFetch(url(route), {
      method: "GET",
      headers: authenticatedHeaders(request),
      ...(request.signal === undefined ? {} : { signal: request.signal }),
    });

  return {
    baseUrl,

    async getServiceIdentity(signal) {
      const response = await get(ROUTES.serviceIdentity, signal);
      if (!response.ok) await problemErrorFrom(response);

      const body = await parseJson(response);
      if (!isServiceIdentityBody(body)) {
        throw new HalyardMalformedResponseError(
          `GET ${ROUTES.serviceIdentity} did not return a ServiceIdentity document.`,
          response.status,
          requestIdFrom(response),
        );
      }
      return body;
    },

    async getHealth(signal) {
      const response = await get(ROUTES.health, signal);
      if (!response.ok) await problemErrorFrom(response);

      const body = await parseJson(response);
      if (!isHealthBody(body)) {
        throw new HalyardMalformedResponseError(
          `GET ${ROUTES.health} did not return a Health document.`,
          response.status,
          requestIdFrom(response),
        );
      }
      return body;
    },

    async getReadiness(signal) {
      const response = await get(ROUTES.readiness, signal);

      // 200 and 503 both carry a Readiness body per the contract. 503 is the
      // API telling us what is wrong, not the transport failing.
      if (response.status === 200 || response.status === 503) {
        const body = await parseJson(response);
        if (isReadinessBody(body)) return body;

        // A 503 that is not a Readiness document did not come from the API —
        // it came from something in front of it. Say so rather than guessing.
        throw new HalyardMalformedResponseError(
          `GET ${ROUTES.readiness} returned HTTP ${response.status} without a Readiness document.`,
          response.status,
          requestIdFrom(response),
        );
      }

      return problemErrorFrom(response);
    },

    async logIn(credentials, signal) {
      const response = await doFetch(url(ROUTES.login), {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify(credentials),
        ...(signal === undefined ? {} : { signal }),
      });

      if (!response.ok) await problemErrorFrom(response);

      const body = await parseJson(response);
      if (!isLoginResponseBody(body)) {
        throw new HalyardMalformedResponseError(
          `POST ${ROUTES.login} did not return a LoginResponse document.`,
          response.status,
          requestIdFrom(response),
        );
      }
      return body;
    },

    async logOut(request) {
      const response = await doFetch(url(ROUTES.logout), {
        method: "POST",
        headers: authenticatedHeaders(request),
        ...(request.signal === undefined ? {} : { signal: request.signal }),
      });

      // The server slid the idle deadline serving this. Mirror that locally so
      // the two clocks stay honest without a single extra request going out.
      request.clock?.noteAuthenticatedRequest();

      if (response.status === 204) return;
      await problemErrorFrom(response);
    },

    async getSession(request) {
      const response = await doFetch(url(ROUTES.session), {
        method: "GET",
        headers: authenticatedHeaders(request),
        ...(request.signal === undefined ? {} : { signal: request.signal }),
      });

      request.clock?.noteAuthenticatedRequest();

      if (!response.ok) await problemErrorFrom(response);

      const body = await parseJson(response);
      if (!isSessionBody(body)) {
        throw new HalyardMalformedResponseError(
          `GET ${ROUTES.session} did not return a Session document.`,
          response.status,
          requestIdFrom(response),
        );
      }
      return body;
    },

    async listMonitors(request) {
      const response = await authenticatedGet(ROUTES.monitors, request);

      // The server slid the idle deadline serving this, exactly as it does for
      // every authenticated request. Mirror it locally; do not add a timer.
      request.clock?.noteAuthenticatedRequest();

      if (!response.ok) await problemErrorFrom(response);

      const body = await parseJson(response);
      if (!isMonitorListBody(body)) {
        throw new HalyardMalformedResponseError(
          `GET ${ROUTES.monitors} did not return a MonitorList document.`,
          response.status,
          requestIdFrom(response),
        );
      }
      return body;
    },

    async getMonitor(id, request) {
      const response = await authenticatedGet(monitorPath(id), request);

      request.clock?.noteAuthenticatedRequest();

      // Note what does NOT happen here: a 404 is passed to problemErrorFrom like
      // any other status and comes back a HalyardNotFoundError. It is never
      // rewritten as a role problem, and the id is never echoed back into the
      // message. See HalyardNotFoundError and REQ-703.
      if (!response.ok) await problemErrorFrom(response);

      const body = await parseJson(response);
      if (!isMonitorBody(body)) {
        throw new HalyardMalformedResponseError(
          `GET ${ROUTES.monitor} did not return a Monitor document.`,
          response.status,
          requestIdFrom(response),
        );
      }
      return body;
    },

    async checkMonitor(request) {
      const route = checkMonitorPath(request.monitorId);

      // The executor's own budget is 30 s overall. Waiting less than that would
      // abort a slow-but-legitimate check just before its answer arrived and
      // report our impatience as the target's failure. When the caller supplies
      // its own signal it owns the deadline; otherwise this one applies.
      const signal = request.signal ?? AbortSignal.timeout(CHECK_TRIGGER_TIMEOUT_MS);

      const response = await doFetch(url(route), {
        method: "POST",
        // No body, and no Content-Type: the operation object has no
        // `requestBody`, so there is nothing to declare a type for.
        headers: authenticatedHeaders(request),
        signal,
      });

      // Authenticated, so the server slid the idle deadline serving it. Mirror
      // that locally. This is the *only* clock interaction here: the route is
      // never polled and never used as a keepalive.
      request.clock?.noteAuthenticatedRequest();

      // Every non-200 is a problem document, 409 and 429 included. 404 stays a
      // HalyardNotFoundError and is never re-dressed as a role denial.
      if (response.status !== 200) await problemErrorFrom(response);

      const body = await parseJson(response);
      if (!isCheckExecutionBody(body)) {
        throw new HalyardMalformedResponseError(
          `POST ${ROUTES.checkMonitor} did not return a CheckExecution document.`,
          response.status,
          requestIdFrom(response),
        );
      }
      return body;
    },
  };
}

/**
 * The credential a `LoginResponse` carries, wrapped so it cannot be confused with
 * any other string. The token itself is never inspected — see `SessionCredential`.
 */
export function credentialFrom(login: LoginResponseBody): SessionCredential {
  return { token: login.token };
}
